/*
===============================================================================
	File        =   dashboard-app/src/app/App.js
	Client      =   Rhealize
	Project     =   Ikimy Platform
	Purpose     =   This file is the react app
				    
===============================================================================
	Revision History
		-----------------------------------------------------------------------
		Version Date        Author              Comments
		-----------------------------------------------------------------------
		1.0     2025.02.06  Moataz Khallaf      Initial Creation
		1.1		2025.02.28	Daniel Rashidian	Set up navigation
		1.2		2025.03.12	Daniel Rashidian	Set up routing for recruiter page
		-----------------------------------------------------------------------
===============================================================================
*/
import "./App.css";
// react
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
	useNavigate,
} from "react-router-dom";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { useLocation } from "react-router-dom";

// Components
import { NavBarDefaultCandidate, FooterNew, FooterMobile } from "../ui-components";
import NavigationMobile from "./NavigationMobile";

// pages
import Candidate from "../pages/Candidate/Candidate";
import Contact from "../pages/Contact/Contact";

// utils
import Error from "../utils/error/Error";
import Recruiter from "../pages/Recruiter/Recruiter";

const CANDIDATE_APP = process.env.REACT_APP_CANDIDATE_APP;
const RECRUITER_APP = process.env.REACT_APP_RECRUITER_APP;

function AppWrapper() {
	return (
		<Router>
			<App />
		</Router>
	);
}

function App() {
	const [errors, setErrors] = useState(new Map());
	const [contactModalVisible, setContactModalVisible] = useState(false);
	const isLargeScreen = useMediaQuery({ query: '(min-width: 768px)' })
	const location = useLocation();
	const navigate = useNavigate();

	const removeError = useCallback((errorMessage) => {
		setErrors((prevErrors) => {
			const newErrors = new Map(prevErrors);
			newErrors.delete(errorMessage);
			return newErrors;
		});
	}, []);

	const addError = useCallback((newError) => {
		setErrors((prevErrors) => {
			const newErrors = new Map(prevErrors);
			newErrors.set(newError.message, Date.now());
			return newErrors;
		});
	}, []);

	useEffect(() => {
		if (errors.size > 0) {
			const oldestErrorMessage = [...errors.entries()].reduce(
				(oldest, current) => {
					return !oldest || current[1] < oldest[1] ? current : oldest;
				}
			)[0];

			const timer = setTimeout(() => {
				removeError(oldestErrorMessage);
			}, 10000);

			return () => clearTimeout(timer);
		}
	}, [errors, removeError]);

	const navBarDefaultCandidateOverrides = useMemo(() => {
		return {
			NavBarDefaultCandidate: {
				width: "100%",
				height: "100px",
			},
			NavMenu: {
				width: "100%",
				shrink: "1"
			},
			TopNavMenuItem725424222: {
				...(location.pathname !== "/candidate") && {
					style: {
						cursor: "pointer"
					},
					onClick: (event) => {
						navigate("/candidate");
					},
				},
				shrink: "1"
			},
			NavText725424223: {
				...(location.pathname !== "/candidate") && {
					color: 'rgba(49,91,155,1)',
				},
				fontWeight: '600',
				textDecoration: 'none',
				shrink: "1",
				children: "For Candidates"
			},

			TopNavMenuItem725424226: {
				...(location.pathname !== "/employer") && {
					style: {
						cursor: "pointer"
					},
					onClick: (event) => {
						navigate("/employer");
					},
				},
				shrink: "1"
			},
			// About 
			NavText725424227: {
				...(location.pathname !== "/employer") && {
					color: 'rgba(49,91,155,1)',
				},
				fontWeight: '600',
				textDecoration: 'none',
				shrink: "1",
				children: "For Employers"
			},
			TopNavMenuItem725424228: {
				display: "none"
			},
			NavText725424229: {
				children: "About",
				shrink: "1",
			},
			TopNavMenuItem725424230: {
				style: {
					cursor: "pointer"
				},
				onClick: (event) => {
					setContactModalVisible(true);
				}
			},
			NavText725424231: {
				fontWeight: '600',
				color: 'rgba(49,91,155,1)',
				textDecoration: 'none',
			},
			"Frame 1160464103": {
				justifyContent: "flex-end"
			},
			TopNavMenuItem725424162: {
				onClick: (event) => {
					window.open(`${location.pathname === "/employer" ? RECRUITER_APP : CANDIDATE_APP}`, "_blank");
				},
				style: {
					cursor: "pointer"
				},
				overrides: {
					NavText: {
						children: "Login",
						fontWeight: '600',
						color: 'rgba(49,91,155,1)',
						textDecoration: 'none',
					}
				}
			},
			Button: {
				onClick: (event) => {
					window.open(`${location.pathname === "/employer" ? RECRUITER_APP : CANDIDATE_APP}/signup`, "_blank");
				},
				children: "Sign Up",
				shrink: "1"
			}
		};
	}, [location, navigate]);

	const footerNewOverrides = useMemo(() => {
		return {
			FooterNew: {
				width: "100%",
				height: "100%",
				justifyContent: "center"
			},
			"Frame 1160464410": {
				shrink: "1",
				width: "100%"
			},
			"Copyright \u00A9 2022 2309225 Alberta Ltd. RHEALIZE All Rights Reserved": {
				width: "unset",
				shrink: "1"
			},
			"Frame 1160464409": {
				shrink: "1",
				width: "100%"
			},
			Overview: {
				width: "unset"
			},
			"For employers": {
				width: "unset",
				style: {
					cursor: "pointer"
				},
				display: "flex",
				gap: "8px",
				children: (
					<>
						<span
							onClick={(event) => {
								navigate("/candidate");
							}}
						>For Candidates
						</span>
						<span
							onClick={(event) => {
								navigate("/employer");
							}}
						>For Employers
						</span>
					</>
				),
			},
			About: {
				width: "unset"
			},
			Contact: {
				width: "unset",
				style: {
					cursor: "pointer"
				},
				children: (
					<span
						onClick={(event) => {
							setContactModalVisible(true);
						}}
					>Contact</span>
				),
			},
			Blog: {
				width: "unset",
			},
			Resources: {
				width: "unset"
			},
			"Privacy Policy": {
				width: "unset"
			},
			"Terms of Use": {
				width: "unset"
			}
		};
	}, [navigate]);

	const footerMobileOverrides = useMemo(() => {
		return {
			FooterMobile: {
				width: "100%",
				maxWidth: "100%",
				justifyContent: "center",
			},
			"For employers": {
				style: {
					cursor: "pointer"
				},
				display: "flex",
				gap: "4px",
				children: (
					<>
						<span
							onClick={(event) => {
								navigate("/candidate");
							}}
						>For Candidates
						</span>
						<span
							onClick={(event) => {
								navigate("/employer");
							}}
						>For Employers
						</span>
					</>
				),
			},
			Contact: {
				style: {
					cursor: "pointer"
				},
				children: (
					<span
						onClick={(event) => {
							setContactModalVisible(true);
						}}
					>Contact</span>
				),
			},
		};
	}, [navigate]);

	return (
		<div className="container">
			<div className="content">
				{/* <Error errors={errors} onClose={removeError}></Error> */}

				<div className={isLargeScreen ? "center-container" : ""}>
					{
						contactModalVisible &&
						<Contact setContactModalVisible={setContactModalVisible}></Contact>
					}
					{isLargeScreen &&
						<NavBarDefaultCandidate overrides={navBarDefaultCandidateOverrides}></NavBarDefaultCandidate>
					}
					{!isLargeScreen &&
						<NavigationMobile
							appLink={location.pathname === "/employer" ? RECRUITER_APP : CANDIDATE_APP}
							setContactModalVisible={setContactModalVisible}
						></NavigationMobile>
					}

					<Routes>
						<Route
							path="/"
							element={<Navigate to="/candidate" replace />}
						/>
						<Route path="/candidate" element={<Candidate />} />
						<Route path="/employer" element={<Recruiter />} />
					</Routes>

					{isLargeScreen &&
						<FooterNew overrides={footerNewOverrides}></FooterNew>
					}
					{!isLargeScreen &&
						<FooterMobile overrides={footerMobileOverrides}></FooterMobile>
					}
				</div>
			</div>
		</div>
	);
}

export default AppWrapper;
